import React, { useEffect, useState } from "react";
import { Loader2, RefreshCw, Server } from "lucide-react";
import { toast } from "sonner";
import { getLocalReview, rerunLocalReview } from "../lib/api";

// Second opinion from the free local model (Ollama). Polls while the
// background job runs — a CPU-only VPS can take a few minutes.
export default function LocalReviewPanel({ estimateId }) {
    const [data, setData] = useState(null);

    useEffect(() => {
        if (!estimateId) return undefined;
        let alive = true;
        let timer;
        const tick = async () => {
            try {
                const d = await getLocalReview(estimateId);
                if (!alive) return;
                setData(d);
                if (d?.review?.status === "running") timer = setTimeout(tick, 10000);
            } catch { /* not an estimating user / older estimate — stay hidden */ }
        };
        tick();
        return () => { alive = false; clearTimeout(timer); };
    }, [estimateId]);

    if (!data?.enabled) return null;
    const r = data.review;

    const rerun = async () => {
        try {
            await rerunLocalReview(estimateId);
            setData({ ...data, review: { status: "running", model: data.model } });
            const poll = async () => {
                const d = await getLocalReview(estimateId);
                setData(d);
                if (d?.review?.status === "running") setTimeout(poll, 10000);
            };
            setTimeout(poll, 10000);
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not start the review"); }
    };

    const score = r?.score;
    const tone = score == null ? "text-slate-300" : score >= 75 ? "text-emerald-300" : score >= 50 ? "text-amber-300" : "text-red-300";
    return (
        <div className="mt-3 rounded-sm border border-emerald-500/30 bg-emerald-500/5 p-3" data-testid="estimate-local-review">
            <div className="flex items-center gap-2">
                <Server className="h-4 w-4 text-emerald-300" />
                <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-300">Second opinion · local {data.model} · free</span>
                {r?.status === "done" && score != null && <span className={`ml-auto font-mono text-lg font-bold ${tone}`} data-testid="estimate-local-review-score">{score}/100</span>}
                {r?.status !== "running" && (
                    <button type="button" onClick={rerun} data-testid="estimate-local-review-rerun" title="Re-check the current line items"
                        className={`${r?.status === "done" && score != null ? "" : "ml-auto"} inline-flex items-center gap-1 rounded border border-white/15 px-2 py-1 text-[10px] uppercase tracking-wider text-slate-200 hover:border-emerald-400`}>
                        <RefreshCw className="h-3 w-3" /> {r ? "Re-check" : "Check now"}
                    </button>
                )}
            </div>
            {!r && <p className="mt-1 text-xs text-slate-400">No review yet for this estimate.</p>}
            {r?.status === "running" && (
                <p className="mt-2 flex items-center gap-2 text-xs text-slate-300" data-testid="estimate-local-review-running">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Reading the bid on your server… this can take a few minutes on CPU. You can close this; it keeps going.
                </p>
            )}
            {r?.status === "error" && <p className="mt-2 text-xs text-red-300" data-testid="estimate-local-review-error">Local model didn't answer: {r.detail}</p>}
            {r?.status === "done" && (
                <div className="mt-2 space-y-2 text-sm text-slate-200">
                    {r.verdict && <p data-testid="estimate-local-review-verdict">{r.verdict}</p>}
                    {r.missing?.length > 0 && <Block title="Possibly missing" items={r.missing} />}
                    {r.double_counted?.length > 0 && <Block title="Possibly double-counted" items={r.double_counted} />}
                    {r.looks_off?.length > 0 && <Block title="Looks off" items={r.looks_off.map((o) => (o.why ? `${o.item} — ${o.why}` : o.item))} />}
                    <p className="text-[10px] text-slate-500">Answered in {r.took_s}s. Advisory only — Claude's numbers above are the quote.</p>
                </div>
            )}
        </div>
    );
}

function Block({ title, items }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">{title}</div>
            <ul className="list-disc list-inside space-y-0.5">{items.map((x, i) => <li key={i}>{x}</li>)}</ul>
        </div>
    );
}
